/**
 * @deprecated TaskRunner is deprecated and should not be used for new implementations.
 *
 * This component uses WebSocket polling (every 0.5s) which is inefficient compared to
 * the new async task framework.
 *
 * Use the new async task framework instead, which provides:
 * - Real-time progress updates via Server-Sent Events (SSE) instead of polling
 * - Built-in React hooks: useAsyncTaskStream, useUserAsyncTask
 * - Type-safe task contracts
 * - Better performance with Redis-backed state
 * - Automatic retry logic and connection handling
 *
 * Migration examples:
 *
 * Old way (deprecated):
 * - Component: <TaskRunner /> (global WebSocket polling)
 * - Hook: useAsyncJobDownload(jobName)
 *
 * New way:
 * - Hook: useUserAsyncTask(ASYNC_TASK_NAMES.EXPORT_COMPANY_MEETINGS_CSV)
 * - Component: <DownloadButtonSSE /> for download with progress
 * - Direct SSE: useAsyncTaskStream(taskId)
 *
 * See documentation:
 * - Async Task Framework Guide: docs/guides/async-task-framework.md
 * - Example: webserver/assets/react/components.smart/DownloadButtonSSE/
 *
 * ---
 *
 * TaskRunner - Modernized WebSocket-powered background jobs monitor
 *
 * This component bridges modern Zustand WebSocket management with legacy Redux state
 * for backward compatibility. It replaces the class-based TaskRunner with a functional
 * component that:
 *
 * 1. Uses Zustand for WebSocket connection management
 * 2. Automatically forwards WebSocket messages to Redux via useWebSocketReduxBridge
 * 3. Maintains full compatibility with existing Redux selectors and components
 * 4. Provides the same UI/UX as the original TaskRunner
 *
 * Migration benefits:
 * - Modern React hooks patterns
 * - Better WebSocket connection handling
 * - Functional and declarative programming approach
 * - Maintains compatibility with older systems
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

// Legacy components
import Config from "./Config";
import JobsList from "./components/JobsList";
import Status from "./components/Status";
import TaskRunnerFallback from "./components/TaskRunnerFallback";

// Zustand WebSocket store and Redux bridge
import { useWebSocketJobsStore, useWebSocketReduxBridge } from "../../api/websocket";

// Redux selectors
import { getIsDebug } from "../../selectors/app";
import { getJobCount, getJobStatus, getJobs, getWebsocketStatus } from "../../selectors/jobs";

import TaskRunnerPortal from "./portal";

const TaskRunner: React.FC = () => {
	// Set up the Zustand -> Redux bridge
	useWebSocketReduxBridge();

	// Zustand WebSocket store
	const { connect, disconnect, allowJobsListToBeClosed, disallowJobsListToBeClosed } = useWebSocketJobsStore();

	// Redux state via selectors (maintaining compatibility with older systems)
	const jobs = useSelector(getJobs);
	const isDebug = useSelector(getIsDebug);
	const jobCount = useSelector(getJobCount);
	const status = useSelector(getJobStatus);
	const failedWebsocket = useSelector(getWebsocketStatus);

	// Local component state
	const [isOpen, setIsOpen] = useState(false);
	const [hasError, setHasError] = useState(false);
	const jobsListCanCloseRef = useRef(true);

	// Connect to WebSocket on mount
	useEffect(() => {
		connect("/api/user_jobs/status/");

		return () => {
			disconnect();
		};
	}, [connect, disconnect]);

	// Document click handler for closing jobs list
	const closeJobsList = useCallback(() => {
		if (jobsListCanCloseRef.current) {
			setIsOpen(false);
		}
	}, []);

	// Set up document event listeners
	const setJobsListState = useCallback(
		(nextState: boolean) => {
			setIsOpen(nextState);

			if (nextState === true) {
				document.addEventListener("click", closeJobsList, false);
			} else {
				document.removeEventListener("click", closeJobsList, false);
			}
		},
		[closeJobsList]
	);

	// Cleanup event listeners on unmount
	useEffect(() => {
		return () => {
			document.removeEventListener("click", closeJobsList, false);
		};
	}, [closeJobsList]);

	// Handle opening jobs list
	const openJobsList = useCallback(() => {
		if (status === "running") {
			setJobsListState(true);
		}
	}, [status, setJobsListState]);

	// Jobs list mouse handlers
	const allowJobsListToBeClosedHandler = useCallback(() => {
		jobsListCanCloseRef.current = true;
		allowJobsListToBeClosed();
	}, [allowJobsListToBeClosed]);

	const disallowJobsListToBeClosedHandler = useCallback(() => {
		jobsListCanCloseRef.current = false;
		disallowJobsListToBeClosed();
	}, [disallowJobsListToBeClosed]);

	// Error boundary functionality
	useEffect(() => {
		const handleError = () => {
			setHasError(true);
		};

		window.addEventListener("error", handleError);
		return () => window.removeEventListener("error", handleError);
	}, []);

	if (hasError) {
		return <div />;
	}

	const { wrapperSize } = Config;
	let jobCountLabel = "";

	if (jobCount === 1) {
		jobCountLabel = `${jobCount} Background Job`;
	} else if (jobCount > 99) {
		jobCountLabel = "99+ Background Jobs";
	} else if (jobCount > 1) {
		jobCountLabel = `${jobCount} Background Jobs`;
	} else {
		jobCountLabel = "No Background Jobs";
	}

	// Production mode: hide when no jobs
	if (process.env.NODE_ENV === "production" && jobCount <= 0) {
		return <div>{failedWebsocket ? <TaskRunnerFallback /> : null}</div>;
	}

	return (
		<div>
			{failedWebsocket ? <TaskRunnerFallback /> : <div />}
			<TaskRunnerPortal
				selector={isDebug ? "react-jobs-body-root" : "react-task-watcher"}
				className={isOpen ? "is-open" : ""}
			>
				<div className="task-running-container" onClick={openJobsList} style={{ padding: 3 }}>
					<div style={{ height: wrapperSize }}>
						<div
							className={`indicator-wrapper ${status}-wrapper`}
							style={{
								height: wrapperSize,
								width: wrapperSize,
							}}
						>
							<Status>{status}</Status>
						</div>

						<div
							style={{
								marginLeft: wrapperSize + 3,
								lineHeight: `${wrapperSize}px`,
							}}
							className={`job-count ${jobCount <= 0 ? "no-jobs" : ""}`}
						>
							{jobCountLabel}
						</div>
					</div>

					{isOpen ? (
						<div
							style={{
								margin: "5px 5px 0 5px",
								height: 1,
								backgroundColor: "rgba(0, 0, 0, 0.15)",
							}}
						/>
					) : (
						<div />
					)}
					{isOpen ? (
						<JobsList
							jobs={jobs}
							onMouseLeave={allowJobsListToBeClosedHandler}
							onMouseEnter={disallowJobsListToBeClosedHandler}
						/>
					) : (
						<div />
					)}
				</div>
			</TaskRunnerPortal>
		</div>
	);
};

/** @deprecated TaskRunner is deprecated and should not be used for new implementations. */
export default TaskRunner;
